import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/common/AdminHeader';
import Dashboard from '../components/Dashboard';
import { useDashboardData } from '../hooks/useDashboardData';
import { useScreenPermission } from '../hooks/useScreenPermission';
import { getSubscriptionSummary } from '../services/subscriptionData';
import { CreditCard, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';

interface SubscriptionSummary {
  Total_Subscriptions: number;
  Active_Subscriptions: number;
  Inactive_Subscriptions: number;
  Expiring_in_10_days: number;
  Expired_Subscriptions: number;
  Ready_For_Verification: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { stats, error, refetch } = useDashboardData();
  const { canView: canViewSubs } = useScreenPermission('subscription data');
  const [subSummary, setSubSummary] = useState<SubscriptionSummary | null>(null);

  const loadSubSummary = useCallback(async () => {
    try {
      const { data } = await getSubscriptionSummary();
      setSubSummary((data as any)?.Summary ?? null);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (canViewSubs) loadSubSummary();
  }, [canViewSubs, loadSubSummary]);

  if (error && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Subscription summary widget */}
        {canViewSubs && subSummary && (
          <div
            className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden mb-8 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/subscription-data')}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-brand-50/60">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-brand-500" />
                <p className="font-bold text-gray-800 text-sm">Subscription Overview</p>
              </div>
              <span className="text-xs text-brand-500 font-semibold">View details →</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-100">
              {[
                { label: 'Total',          value: subSummary.Total_Subscriptions,      icon: <CreditCard className="w-3.5 h-3.5" />,    color: 'text-gray-700'   },
                { label: 'Active',         value: subSummary.Active_Subscriptions,     icon: <CheckCircle className="w-3.5 h-3.5" />,   color: 'text-green-600'  },
                { label: 'Inactive',       value: subSummary.Inactive_Subscriptions,   icon: <XCircle className="w-3.5 h-3.5" />,       color: 'text-gray-400'   },
                { label: 'Expiring Soon',  value: subSummary.Expiring_in_10_days,      icon: <Clock className="w-3.5 h-3.5" />,         color: 'text-amber-600'  },
                { label: 'Expired',        value: subSummary.Expired_Subscriptions,    icon: <AlertCircle className="w-3.5 h-3.5" />,   color: 'text-red-500'    },
                { label: 'Ready to Verify',value: subSummary.Ready_For_Verification,   icon: <CheckCircle className="w-3.5 h-3.5" />,   color: 'text-brand-500'   },
              ].map((s) => (
                <div key={s.label} className="bg-white px-4 py-3 text-center">
                  <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                  <div className={`flex items-center justify-center gap-1 mt-0.5 ${s.color}`}>
                    {s.icon}
                    <p className="text-xs font-medium text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Dashboard />
      </main>
    </div>
  );
}
